/* eslint-disable import/no-unresolved */
import { useMemo, useState, useRef } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import { cx } from '@emotion/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Skeleton } from '@mui/material';
import { Pagination, Zoom } from 'swiper/modules';
import type { Swiper as SwiperCore } from 'swiper';
import colors from '@/theme/colors';
import { FlyerStatus } from '@/types/flyer';
import { Divider, LiveFeedback } from '@/components';
import { BodySmallEmphasized } from '@/design-system';
import { CallToAction, Flyer } from '@/graphql/types';
import config from '@/config/config';
import { TrackingFrom, useLiveFeedback } from '@/hooks';
import { AidaStatus } from '@/config/constants';
import InteractiveFlyer from '../InteractiveFlyer/InteractiveFlyer';
import styles from './FlyerDetailsSwiper.styles';

interface Props {
  flyer: Flyer;
  loadingFlyer: boolean;
  isExpired?: boolean;
  isPending?: boolean;
  audienceId?: string | null;
  onLoadFlyer: () => void;
  slideSwiper: () => void;
  trackingFrom: TrackingFrom;
  flyerTitle: string;
  aidaStatus?: AidaStatus;
}

export const FlyerDetailsSwiper: React.FC<Props> = ({
  flyer,
  loadingFlyer,
  isExpired = false,
  isPending = false,
  onLoadFlyer,
  slideSwiper,
  trackingFrom,
  flyerTitle,
  aidaStatus = AidaStatus.off,
}) => {
  const { pages, flyerId, districtId, ctas, pageCount } = flyer;
  const featuredLinkIds = flyer.featuredLinkIds ?? [];
  const [activeSlide, setActiveSlide] = useState<number>(1);
  const [highlightedLink, highlightLink] = useState<CallToAction['id']>();

  const [feedbackMessage, setFeedbackMessage] = useLiveFeedback({ delay: 2000 });
  const slidesPerView = useMemo(() => (pages.length > 1 ? 1.08 : 1), [pages]);
  const spaceBetween = useMemo(() => (slidesPerView === 1 ? 0 : 10), [slidesPerView]);
  const swiperRef = useRef<SwiperCore | null>(null);

  const handleSlideChange = (core: SwiperCore) => {
    const newActiveIndex = core.activeIndex + 1;
    setActiveSlide(newActiveIndex);
    slideSwiper();
    setFeedbackMessage(`Showing slide ${newActiveIndex} of ${pages.length}`);
  };

  const handlePaginationClick = (index: number) => {
    swiperRef.current?.slideTo(index);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowLeft') {
      swiperRef.current?.slideTo(index - 1);
    } else if (event.key === 'ArrowRight') {
      swiperRef.current?.slideTo(index + 1);
    }
  };

  const renderCustomPagination = () => (
    <ul className={styles.customPagination}>
      {pages.map((page, index) => (
        <li key={page.pageNumber}>
          <button
            type="button"
            tabIndex={0}
            className={cx(styles.paginationButton, {
              [styles.paginationButtonActive]: index + 1 === activeSlide,
            })}
            aria-label={`Slide ${index + 1}`}
            aria-current={index + 1 === activeSlide ? 'true' : undefined}
            onClick={() => handlePaginationClick(index)}
            onKeyDown={event => handleKeyDown(event, index)}
          >
            <span className="sr-only">{`Slide ${index + 1}`}</span>
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <div>
        {loadingFlyer && featuredLinkIds.length === 0 && (
          <Skeleton variant="rectangular" width="100%" height={350} />
        )}
        <LiveFeedback message={feedbackMessage} />
        <Divider />
        {aidaStatus === AidaStatus.off && !loadingFlyer && (
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {`Text Found On Flyer - ${pages[activeSlide - 1]?.userText || 'No Text Available'}`}
          </div>
        )}
        <Swiper
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
          zoom
          simulateTouch
          slidesPerView={slidesPerView}
          spaceBetween={spaceBetween}
          centeredSlides={false}
          modules={[Pagination, Zoom]}
          className={styles.swiper}
          onSlideChange={handleSlideChange}
          data-testid="slides"
          pagination={{
            el: '.custom-pagination',
            clickable: true,
          }}
          role="region"
          aria-label="Flyer slides"
        >
          {pages.map((page, index) => {
            const ariaDescribedby = `flyer-Description-Details-${page.pageNumber}`;
            const slideLabel = `slide-${index + 1}-label`;
            const slideKey = `slide-${page.pageNumber}`;
            return (
              // eslint-disable-next-line jsx-a11y/no-redundant-roles -- Swiper renders a div wrapper, not a ul, so li has no implicit listitem role here
              <li key={slideKey} role="listitem">
                <SwiperSlide
                  id={`slide-${index + 1}`}
                  aria-roledescription="slide"
                  aria-labelledby={slideLabel}
                  data-testid="slideItem"
                  key={slideKey}
                >
                  <>
                    {aidaStatus === AidaStatus.off && (
                      <span id={ariaDescribedby} className="sr-only">
                        Text Found On Flyer - {page.userText || 'No Text Available'}
                      </span>
                    )}
                    <span id={slideLabel} className="sr-only">
                      Slide {index + 1} of {pages.length}
                    </span>
                    {featuredLinkIds.length > 0 ? (
                      <InteractiveFlyer
                        key={`page${activeSlide}`}
                        flyerId={flyerId}
                        districtId={districtId}
                        pageNumber={activeSlide}
                        flyerImage={page.imageUrl}
                        highlightedLink={highlightedLink}
                        highlightLink={highlightLink}
                        isExpired={isExpired}
                        isPending={isPending}
                        ctas={ctas}
                        featuredLinkIds={featuredLinkIds}
                        onLoadFlyer={onLoadFlyer}
                        loadingFlyer={loadingFlyer}
                        aria-describedby={ariaDescribedby}
                        trackingFrom={trackingFrom}
                        distributionsReleased={flyer.distributionsReleased}
                      />
                    ) : (
                      <div className="swiper-zoom-container">
                        <img
                          src={`https://flyers-bff.${config.PEACHJAR_DOMAIN}/view/district/${districtId}/flyer/${flyerId}/${page.pageNumber}.jpg`}
                          className={cx(
                            styles.flyer,
                            loadingFlyer && styles.hide,
                            isExpired && styles.expiredFlyerOverlay,
                            isPending && styles.pendingFlyerOverlay
                          )}
                          alt={`Image of a flyer titled ${flyerTitle}`}
                          aria-describedby={ariaDescribedby}
                          onLoad={onLoadFlyer}
                        />
                      </div>
                    )}
                  </>
                </SwiperSlide>
              </li>
            );
          })}
        </Swiper>
      </div>
      {!loadingFlyer && (flyer.wcagHtml || pageCount > 1) && flyer.status === FlyerStatus.active && (
        <div className={styles.paginationContainer}>
          <>
            {pageCount > 1 && (
              <div className={styles.paginationPadding}>
                <div className={styles.swiperPagination}>
                  <BodySmallEmphasized color={colors.slate} data-testid="pageNumber">
                    {`${activeSlide} of ${pageCount}`}
                  </BodySmallEmphasized>
                </div>
                {renderCustomPagination()}
              </div>
            )}
          </>
        </div>
      )}
    </>
  );
};
